import React from "react";
import { Box, Button, Link, Typography } from "@mui/material";
import { SellerPayoutContext, WithdrawalRequest } from "../../../common/requests/transactions/endpoints";
import { formatDate } from "../../../common/constant/dates";
import { C } from "../../../common/theme";
import { formatAge, formatMoney } from "../../../common/utils/format";
import { Banner, CopyValue, DetailDialog, DetailList, DetailSection, EmptyState, StatusPill } from "../../../ui";
import { isPaid, payoutLineTotals, statusLabel, whatsappUrl } from "./payoutUtils";
import PayoutOrders, { programmeNote } from "./PayoutOrders";
import { PayToDetails } from "../../../components/PayoutKey";

interface PayoutDialogProps {
  open: boolean;
  /** Still loading the list, so a missing id may yet turn up */
  loading: boolean;
  request: WithdrawalRequest | null;
  seller?: SellerPayoutContext;
  /** The unlocked account number, when the owner has unlocked this tab */
  plainAccount?: string;
  payoutsRecorded: boolean;
  canPay: boolean;
  onPay: (request: WithdrawalRequest) => void;
  onClose: () => void;
}

/** One withdrawal request: what to pay, where it goes and how it was paid.
 * Seller balances are left out on purpose: earnings stay "in withdrawal" after
 * they're paid, so that total adds up every past payout and reads as if far
 * more were owed than this request. */
export default function PayoutDialog({ open, loading, request: r, seller, plainAccount, payoutsRecorded, canPay, onPay, onClose }: PayoutDialogProps) {
  if (!r) {
    return (
      <DetailDialog open={open} onClose={onClose} title="Payout" size="sm" loading={loading}>
        <EmptyState title="Payout not found." body="It may have been removed, or the link is wrong. Check the All tab." />
      </DetailDialog>
    );
  }

  const paid = isPaid(r);
  const name = r.username ?? r.email ?? r.user_id;
  // Bring Your Buyers: 0% fee orders and welcome vouchers change the amount.
  const lines = r.currency_mixed ? null : r.earnings ?? null;
  const note = lines ? programmeNote(payoutLineTotals(lines), r.currency) : null;

  return (
    <DetailDialog
      open={open}
      onClose={onClose}
      title={name}
      status={<StatusPill tone={paid ? "success" : "warning"}>{statusLabel(r)}</StatusPill>}
      subtitle={[r.username && r.email ? r.email : null, seller?.country, `requested ${formatDate(r.request_date)}`].filter(Boolean).join(" · ")}
      actions={
        canPay && !paid ? (
          <Button variant="contained" color="secondary" disableElevation onClick={() => onPay(r)} sx={{ minHeight: 44 }}>
            Mark paid
          </Button>
        ) : undefined
      }
    >
      <DetailSection title="Request">
        <DetailList
          items={[
            {
              label: "Amount",
              value: (
                <Typography variant="h5" component="span">
                  {formatMoney(r.amount, r.currency)}
                </Typography>
              ),
            },
            { label: "Requested", value: `${formatDate(r.request_date)} · ${formatAge(r.request_date)}` },
            {
              label: "Paid before",
              value: seller?.paid.length
                ? seller.paid.map((p) => `${formatMoney(p.amount, p.currency)} in ${p.count} payout${p.count === 1 ? "" : "s"}`).join(" + ")
                : "First payout",
            },
            { label: "Withdrawal ID", value: <CopyValue value={r.withdrawal_id} mono />, full: true },
          ]}
        />
        {note && (
          <Typography variant="body2" sx={{ color: C.ink2 }}>
            {note}
          </Typography>
        )}
        {r.currency_mixed && (
          <Banner tone="warning" title="Mixed currencies.">
            This seller's withdrawn earnings are in more than one currency, so this total mixes them. Check the earnings below before paying.
          </Banner>
        )}
      </DetailSection>

      <DetailSection title="Pay to">
        {seller?.payout_account ? (
          <PayToDetails account={seller.payout_account} plain={plainAccount} />
        ) : (
          <Typography variant="body2" sx={{ color: C.coralText, fontWeight: 600 }}>
            No payout details saved yet. Ask the seller to add them under Settings › Selling, or on WhatsApp.
          </Typography>
        )}
        <DetailList
          items={[
            {
              label: "WhatsApp",
              value: seller?.whatsapp_number ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <CopyValue value={seller.whatsapp_number} mono />
                  <Link href={whatsappUrl(seller.whatsapp_number)} target="_blank" rel="noopener noreferrer" underline="hover">
                    Open chat
                  </Link>
                </Box>
              ) : (
                <Box component="span" sx={{ color: C.muted }}>
                  No number on file
                </Box>
              ),
            },
            // The payout account already names its country.
            ...(seller?.payout_account ? [] : [{ label: "Country", value: seller?.country ?? "–" }]),
          ]}
        />
      </DetailSection>

      {lines && lines.length > 0 && (
        <DetailSection title={`Orders in this payout (${lines.length})`}>
          <PayoutOrders lines={lines} currency={r.currency} />
        </DetailSection>
      )}

      <DetailSection title="How it was paid">
        {paid ? (
          r.payout ? (
            <DetailList
              items={[
                { label: "Reference", value: <CopyValue value={r.payout.payout_reference} mono /> },
                { label: "Paid on", value: formatDate(r.payout.paid_at) },
                { label: "Recorded by", value: r.payout.paid_by_email ?? "–" },
                ...(r.payout.amount_paid !== null && r.payout.amount_paid !== undefined
                  ? [{ label: "Amount paid", value: formatMoney(r.payout.amount_paid, r.payout.currency ?? r.currency) }]
                  : []),
                ...(r.payout.note ? [{ label: "Note", value: r.payout.note, full: true }] : []),
              ]}
            />
          ) : (
            <Typography variant="body2" sx={{ color: C.muted }}>
              {payoutsRecorded ? "Paid before payout records were kept." : "Payout records aren't installed yet."}
            </Typography>
          )
        ) : (
          <Typography variant="body2" sx={{ color: C.muted }}>
            Not paid yet · requested {formatAge(r.request_date)}
            {!canPay ? ". Only superadmins can mark payouts as paid." : "."}
          </Typography>
        )}
      </DetailSection>

    </DetailDialog>
  );
}
